import {
  DEPTH_SIGMA,
  demoFactors,
  HDR_SCALE,
  hexToRgb,
  lightCount,
  linearColor,
  linesStrokeWidth,
  type Scene,
  type SceneRun,
  saturationMatrix,
  type ViewMode,
  WASH_GAIN,
} from '@better-lights/core';
import {
  EMITTER_STRIDE,
  emitterInstances,
  LDR_EMITTER_STRIDE,
  ldrEmitterInstances,
  ldrWashInstances,
  premulHdr,
  runDepthOf,
  SEGMENT_STRIDE,
  segmentInstances,
  WASH_STRIDE,
  washInstances,
  washParams,
  washPerLed,
} from '../instances.ts';
import type { DepthMap, Pixels, PresentFrame, Renderer } from '../types.ts';
import {
  createProgram,
  createTarget,
  createTexture,
  deleteTarget,
  GLError,
  halfFloatRenderable,
  type Program,
  type Target,
} from './gl.ts';
import * as S from './shaders.ts';

type Uniform = number | readonly number[] | Float32Array;

interface Instanced {
  program: Program;
  vao: WebGLVertexArrayObject;
  buffer: WebGLBuffer;
}

export interface WebGL2RendererOptions {
  /** Force the 8-bit LDR pipeline (testing the fallback). */
  forceLdr?: boolean;
  onContextLost?: () => void;
  onContextRestored?: () => void;
}

const CANVAS_GRAY = 5 / 255; // #050505

export class WebGL2Renderer implements Renderer {
  readonly backend = 'webgl2' as const;
  readonly hdr: boolean;
  lost = false;

  private readonly gl: WebGL2RenderingContext;
  private readonly empty: WebGLVertexArrayObject;
  private readonly p: Record<
    | 'grade'
    | 'albedo'
    | 'vignette'
    | 'resolve'
    | 'copy'
    | 'coverage'
    | 'gradeLdr'
    | 'albedoLdr'
    | 'linesPhoto'
    | 'present'
    | 'solid',
    Program
  >;
  private readonly wash: Instanced;
  private readonly emitter: Instanced;
  private readonly segment: Instanced;
  private readonly circle: Instanced;
  private readonly ldrWash: Instanced;
  private readonly ldrEmitter: Instanced;

  private photo: { tex: WebGLTexture; width: number; height: number } | null = null;
  private depthTex: WebGLTexture | null = null;
  private depth: DepthMap | null = null;
  private readonly white: WebGLTexture;
  private targets: {
    w: number;
    h: number;
    scene: Target;
    mask: Target;
    hdr: Target | null;
    wash: Target | null;
    layer: Target | null;
  } | null = null;
  private rendered = false;
  private readonly onLost: (e: Event) => void;
  private readonly onRestored: () => void;

  constructor(
    private readonly canvas: HTMLCanvasElement | OffscreenCanvas,
    opts: WebGL2RendererOptions = {},
  ) {
    const gl = canvas.getContext('webgl2', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    }) as WebGL2RenderingContext | null;
    if (!gl) throw new GLError('WebGL2 is not available in this browser.');
    this.gl = gl;
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);

    this.onLost = (e: Event) => {
      e.preventDefault();
      this.lost = true;
      opts.onContextLost?.();
    };
    this.onRestored = () => opts.onContextRestored?.();
    canvas.addEventListener('webglcontextlost', this.onLost as EventListener);
    canvas.addEventListener('webglcontextrestored', this.onRestored);

    const prog = (name: string, vs: string, fs: string) => createProgram(gl, name, vs, fs);
    this.p = {
      grade: prog('gradeHdr', S.FULLSCREEN_VS, S.GRADE_HDR_FS),
      albedo: prog('albedoHdr', S.FULLSCREEN_VS, S.ALBEDO_HDR_FS),
      vignette: prog('vignette', S.FULLSCREEN_VS, S.VIGNETTE_FS),
      resolve: prog('resolve', S.FULLSCREEN_VS, S.RESOLVE_FS),
      copy: prog('copy', S.FULLSCREEN_VS, S.COPY_FS),
      coverage: prog('coverage', S.FULLSCREEN_VS, S.COVERAGE_FS),
      gradeLdr: prog('nightGrade', S.FULLSCREEN_VS, S.GRADE_LDR_FS),
      albedoLdr: prog('albedo', S.FULLSCREEN_VS, S.ALBEDO_LDR_FS),
      linesPhoto: prog('linesPhoto', S.FULLSCREEN_VS, S.LINES_PHOTO_FS),
      present: prog('present', S.PRESENT_VS, S.PRESENT_FS),
      solid: prog('solid', S.FULLSCREEN_VS, S.SOLID_FS),
    };
    this.empty = gl.createVertexArray()!;
    this.wash = this.instanced(prog('wash', S.WASH_VS, S.WASH_FS), WASH_STRIDE, [
      ['a_center', 2],
      ['a_color', 3],
    ]);
    this.emitter = this.instanced(prog('emitter', S.EMITTER_VS, S.EMITTER_FS), EMITTER_STRIDE, [
      ['a_center', 2],
      ['a_color', 3],
      ['a_p0', 4],
      ['a_p1', 2],
    ]);
    this.segment = this.instanced(prog('segment', S.SEGMENT_VS, S.SEGMENT_FS), SEGMENT_STRIDE, [
      ['a_a', 2],
      ['a_b', 2],
      ['a_flags', 2],
    ]);
    this.circle = this.instanced(prog('circle', S.CIRCLE_VS, S.CIRCLE_FS), 2, [['a_center', 2]]);
    this.ldrWash = this.instanced(prog('ldrWash', S.LDR_WASH_VS, S.LDR_WASH_FS), WASH_STRIDE, [
      ['a_center', 2],
      ['a_color', 3],
    ]);
    this.ldrEmitter = this.instanced(prog('ldrEmitter', S.LDR_EMITTER_VS, S.LDR_EMITTER_FS), LDR_EMITTER_STRIDE, [
      ['a_center', 2],
      ['a_kind', 3],
      ['a_c', 3],
      ['a_m72', 3],
      ['a_m25', 3],
    ]);

    this.white = createTexture(gl, 1, 1, gl.R8, gl.RED, gl.UNSIGNED_BYTE);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 1, 1, gl.RED, gl.UNSIGNED_BYTE, Uint8Array.of(255));

    this.hdr = !opts.forceLdr && this.probeHdr();
  }

  /* ---------------------------------------------------------------- public API */

  setPhoto(photo: ImageBitmap): void {
    const gl = this.gl;
    const max = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    if (photo.width > max || photo.height > max) {
      throw new GLError(`Could not create a ${photo.width}×${photo.height} drawing surface.`);
    }
    if (this.photo) gl.deleteTexture(this.photo.tex);
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, photo);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.photo = { tex, width: photo.width, height: photo.height };
    this.rendered = false;
  }

  setDepth(depth: DepthMap | null): void {
    const gl = this.gl;
    if (this.depthTex) gl.deleteTexture(this.depthTex);
    this.depthTex = null;
    this.depth = depth;
    if (!depth) return;
    this.depthTex = createTexture(gl, depth.width, depth.height, gl.R8, gl.RED, gl.UNSIGNED_BYTE, gl.LINEAR);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, depth.width, depth.height, gl.RED, gl.UNSIGNED_BYTE, depth.data);
  }

  render(scene: Scene, view: ViewMode): void {
    if (this.lost || !this.photo) return;
    this.ensureTargets(scene.width, scene.height);
    if (view === 'lines') this.drawLines(scene);
    else if (this.hdr) this.drawDemoHdr(scene);
    else this.drawDemoLdr(scene);
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.targets!.scene.tex);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    this.rendered = true;
  }

  present(f: PresentFrame): void {
    const gl = this.gl;
    if (this.lost) return;
    const W = this.canvas.width;
    const H = this.canvas.height;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, W, H);
    gl.disable(gl.BLEND);
    gl.disable(gl.SCISSOR_TEST);
    gl.clearColor(CANVAS_GRAY, CANVAS_GRAY, CANVAS_GRAY, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (!this.rendered || !this.targets || !this.photo) return;
    const { s, tx, ty } = f.transform;
    const d = f.dpr;
    const iw = this.targets.w;
    const ih = this.targets.h;
    const rect = [tx * d, ty * d, iw * s * d, ih * s * d];
    this.blit(this.targets.scene.tex, rect, null, null);
    if (f.compareX !== null) {
      const cx = Math.max(0, Math.round(f.compareX * d));
      if (cx > 0) {
        gl.enable(gl.SCISSOR_TEST);
        gl.scissor(0, 0, cx, H);
        this.blit(this.photo.tex, rect, null, null);
        gl.disable(gl.SCISSOR_TEST);
      }
    }
    const l = f.loupe;
    if (l) {
      const clip = [l.cx * d, l.cy * d, l.radius * d];
      const box = [(l.cx - l.radius) * d, (l.cy - l.radius) * d, l.radius * 2 * d, l.radius * 2 * d];
      this.blit(this.targets.scene.tex, box, clip, [CANVAS_GRAY, CANVAS_GRAY, CANVAS_GRAY, 1]);
      const k = l.scale;
      this.blit(
        this.targets.scene.tex,
        [(l.cx - l.ix * k) * d, (l.cy - l.iy * k) * d, iw * k * d, ih * k * d],
        clip,
        null,
      );
    }
  }

  readScene(): Pixels {
    const gl = this.gl;
    if (!this.targets || !this.rendered) throw new GLError('Nothing has been rendered yet.');
    const { w, h, scene } = this.targets;
    const data = new Uint8Array(w * h * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, scene.fbo);
    gl.pixelStorei(gl.PACK_ALIGNMENT, 1);
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, data);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { width: w, height: h, data };
  }

  resize(cssW: number, cssH: number, dpr: number): void {
    const w = Math.max(1, Math.round(cssW * dpr));
    const h = Math.max(1, Math.round(cssH * dpr));
    if (this.canvas.width !== w) this.canvas.width = w;
    if (this.canvas.height !== h) this.canvas.height = h;
  }

  dispose(): void {
    const gl = this.gl;
    this.canvas.removeEventListener('webglcontextlost', this.onLost as EventListener);
    this.canvas.removeEventListener('webglcontextrestored', this.onRestored);
    this.disposeTargets();
    if (this.photo) gl.deleteTexture(this.photo.tex);
    if (this.depthTex) gl.deleteTexture(this.depthTex);
    gl.deleteTexture(this.white);
    for (const p of Object.values(this.p)) gl.deleteProgram(p.program);
    for (const i of [this.wash, this.emitter, this.segment, this.circle, this.ldrWash, this.ldrEmitter]) {
      gl.deleteProgram(i.program.program);
      gl.deleteBuffer(i.buffer);
      gl.deleteVertexArray(i.vao);
    }
    gl.deleteVertexArray(this.empty);
  }

  /* -------------------------------------------------------------- HDR pipeline */

  /** docs/spec/03 §5.2 drawDemoHdr. */
  private drawDemoHdr(scene: Scene): void {
    const gl = this.gl;
    const t = this.targets!;
    const hdr = t.hdr!;
    const f = demoFactors(scene.time, scene.style.brightness);
    const g = f.grade;
    const size = [scene.width, scene.height];
    const hasDepth = this.depth !== null;

    // 1–2. Base: graded photo, linear × HDR_SCALE.
    this.bind(hdr);
    gl.disable(gl.BLEND);
    this.fullscreen(this.p.grade, {
      u_size: size,
      u_exposure: g.exposure,
      u_skyExposure: g.skyExposure,
      u_desat: g.desat,
      u_blue: g.blue,
      u_dusk: g.dusk,
      u_skyAmount: g.skyAmount,
      u_hasDepth: hasDepth ? 1 : 0,
      u_scale: HDR_SCALE,
    });

    if (lightCount(scene) > 0) {
      const gain = f.gain;
      // 3a. Wall wash × albedo, one layer per run when depth gates it.
      const wp = washParams(scene);
      const perLed = washPerLed(scene, gain);
      const groups: SceneRun[][] = hasDepth ? scene.runs.map((r) => [r]) : [scene.runs];
      for (const group of groups) {
        if (!group.some((r) => r.lights.length > 0)) continue;
        this.bind(t.wash!);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        this.drawInstanced(this.wash, washInstances(group, perLed), WASH_STRIDE, {
          u_size: size,
          u_quad: wp.quad,
          u_d0: wp.d0,
          u_reach: wp.reach,
        });
        gl.blendFuncSeparate(gl.ZERO, gl.SRC_COLOR, gl.ZERO, gl.SRC_ALPHA); // Modulate
        const runDepth = this.depth ? runDepthOf(this.depth, scene, group[0]) : 0;
        this.fullscreen(this.p.albedo, {
          u_size: size,
          u_hasDepth: hasDepth ? 1 : 0,
          u_runDepth: runDepth,
          u_depthSigma: DEPTH_SIGMA,
        });
        this.bind(hdr);
        gl.blendFunc(gl.ONE, gl.ONE); // restore with Plus
        this.copy(t.wash!.tex);
      }

      // 3b. Hardware (track + housings), fading into the dark.
      if (f.drawHardware) {
        const r = scene.coreRadius;
        const track = premulHdr(linearColor('#3A3936'), 0.38 * f.hardware);
        const housing = premulHdr(linearColor('#22211F'), 0.6 * f.hardware);
        for (const run of scene.runs) {
          this.strokeRun(run, Math.max(1, r * 1.4) / 2, true, track, hdr, size);
          this.circles(run.lights, r * 1.2, housing, hdr, size);
        }
      }

      // 3c. Emitters, added straight to the exposure.
      this.bind(hdr);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE);
      this.drawInstanced(this.emitter, emitterInstances(scene, gain), EMITTER_STRIDE, { u_size: size });
    }

    // 4. Vignette (Modulate).
    if (f.drawVignette) {
      this.bind(hdr);
      gl.enable(gl.BLEND);
      gl.blendFuncSeparate(gl.ZERO, gl.SRC_COLOR, gl.ZERO, gl.SRC_ALPHA);
      this.fullscreen(this.p.vignette, { u_size: size, u_amount: g.vignette });
    }

    // 5–6. Restore ×16 → sRGB into the 8-bit scene, grain overlay in display space.
    this.bind(t.scene);
    gl.disable(gl.BLEND);
    this.fullscreen(this.p.resolve, { u_grain: f.drawGrain ? f.grainAmount : 0 }, [['u_hdr', hdr.tex]]);
  }

  /* -------------------------------------------------------------- LDR fallback */

  /** docs/spec/03 §6 (8-bit, display-referred). */
  private drawDemoLdr(scene: Scene): void {
    const gl = this.gl;
    const t = this.targets!;
    const f = demoFactors(scene.time, scene.style.brightness);
    const g = f.grade;
    const size = [scene.width, scene.height];
    this.bind(t.scene);
    gl.disable(gl.BLEND);
    this.fullscreen(this.p.gradeLdr, {
      u_size: size,
      u_exposure: g.exposure,
      u_skyExposure: g.skyExposure,
      u_desat: g.desat,
      u_blue: g.blue,
      u_dusk: g.dusk,
      u_skyAmount: g.skyAmount,
      u_grain: g.grain,
      u_vignette: g.vignette,
    });
    if (lightCount(scene) === 0) return;
    const brightness = scene.style.brightness;
    const layer = t.layer!;
    if (f.ldrWashVisibility > 0.01) {
      const L = scene.washLength;
      const intensity = f.ldrWashVisibility * brightness;
      const perLed = Math.min(0.95, 0.3 * intensity * (scene.style.layout === 'pair' ? 0.6 : 1));
      const halfW = L * 0.52;
      this.bind(layer);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE);
      this.drawInstanced(this.ldrWash, ldrWashInstances(scene.runs), WASH_STRIDE, {
        u_size: size,
        u_quad: [-halfW, -L * 0.12, 2 * halfW, L * 1.25],
        u_L: L,
        u_perLed: perLed,
      });
      gl.blendFuncSeparate(gl.ZERO, gl.SRC_COLOR, gl.ZERO, gl.SRC_ALPHA);
      this.fullscreen(this.p.albedoLdr, { u_size: size, u_gain: WASH_GAIN });
      this.bind(t.scene);
      gl.blendFunc(gl.ONE, gl.ONE);
      this.copy(layer.tex);
    }
    if (f.ldrHardware > 0.05) {
      const r = scene.coreRadius;
      const track = premulSrgb('#2B2A28', 0.32 * f.ldrHardware);
      const housing = premulSrgb('#1C1B1A', 0.55 * f.ldrHardware);
      for (const run of scene.runs) {
        this.strokeRun(run, Math.max(1, r * 1.5) / 2, true, track, t.scene, size);
        this.circles(run.lights, r * 1.25, housing, t.scene, size);
      }
    }
    this.bind(layer);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    this.drawInstanced(this.ldrEmitter, ldrEmitterInstances(scene, f.ldrNight, brightness), LDR_EMITTER_STRIDE, {
      u_size: size,
    });
    this.bind(t.scene);
    gl.blendFunc(gl.ONE, gl.ONE);
    this.copy(layer.tex);
  }

  /* ---------------------------------------------------------------- lines view */

  /** docs/spec/03 §7.1 drawLines (the legend strip is drawn by the export code). */
  private drawLines(scene: Scene): void {
    const gl = this.gl;
    const t = this.targets!;
    const size = [scene.width, scene.height];
    const m = saturationMatrix(0.55, 0.78);
    const mat3: number[] = [];
    for (let c = 0; c < 3; c++) for (let r = 0; r < 3; r++) mat3.push(m[r * 5 + c]);
    this.bind(t.scene);
    gl.disable(gl.BLEND);
    this.fullscreen(this.p.linesPhoto, { u_size: size, u_matrix: mat3 });
    const w = linesStrokeWidth(scene);
    const halo: [number, number, number, number] = [0, 0, 0, 0.55];
    for (const run of scene.runs) {
      const color = premulSrgb(run.lineColor, 1);
      this.strokeRun(run, (1.9 * w) / 2, false, halo, t.scene, size);
      this.strokeRun(run, w / 2, false, color, t.scene, size);
      if (run.pts.length === 0) continue;
      for (const end of [run.pts[0], run.pts[run.pts.length - 1]]) {
        this.circles([end], 1.25 * w, halo, t.scene, size);
        this.circles([end], 0.85 * w, color, t.scene, size);
      }
    }
  }

  /* ------------------------------------------------------------------- helpers */

  /** Stroke a run's path as one shape: coverage (union of capsules) → color, SrcOver. */
  private strokeRun(
    run: SceneRun,
    hw: number,
    butt: boolean,
    color: readonly number[],
    target: Target,
    size: number[],
  ): void {
    const seg = segmentInstances(run.pts, butt);
    if (seg.length === 0 || color[3] <= 0) return;
    const gl = this.gl;
    const t = this.targets!;
    this.bind(t.mask);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.enable(gl.BLEND);
    gl.blendEquation(gl.MAX);
    gl.blendFunc(gl.ONE, gl.ONE);
    this.drawInstanced(this.segment, seg, SEGMENT_STRIDE, { u_size: size, u_hw: hw });
    gl.blendEquation(gl.FUNC_ADD);
    this.bind(target);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    this.fullscreen(this.p.coverage, { u_color: color }, [['u_mask', t.mask.tex]]);
  }

  /** Filled AA circles (each drawn separately with SrcOver, like drawCircle). */
  private circles(
    points: readonly { x: number; y: number }[],
    radius: number,
    color: readonly number[],
    target: Target,
    size: number[],
  ): void {
    if (points.length === 0 || color[3] <= 0) return;
    const gl = this.gl;
    this.bind(target);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    const data = new Float32Array(points.length * 2);
    points.forEach((p, i) => {
      data[i * 2] = p.x;
      data[i * 2 + 1] = p.y;
    });
    this.drawInstanced(this.circle, data, 2, { u_size: size, u_radius: radius, u_color: color });
  }

  private probeHdr(): boolean {
    const gl = this.gl;
    if (!halfFloatRenderable(gl)) return false;
    try {
      const t = createTarget(gl, 4, 4, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT, gl.NEAREST);
      this.bind(t);
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE);
      // 16 additions of 1/256: an 8-bit target would also get 0.0625, so add 1/4096 (below 8-bit resolution).
      for (let i = 0; i < 16; i++) this.fullscreen(this.p.solid, { u_color: [1 / 4096, 1 / 4096, 1 / 4096, 0] });
      gl.disable(gl.BLEND);
      const out = new Float32Array(4);
      gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.FLOAT, out);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      deleteTarget(gl, t);
      return Math.abs(out[0] - 16 / 4096) < 1e-3 && gl.getError() === gl.NO_ERROR;
    } catch {
      return false;
    }
  }

  private ensureTargets(w: number, h: number): void {
    if (this.targets && this.targets.w === w && this.targets.h === h) return;
    this.disposeTargets();
    const gl = this.gl;
    const scene = createTarget(gl, w, h, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.LINEAR_MIPMAP_LINEAR);
    const mask = createTarget(gl, w, h, gl.R8, gl.RED, gl.UNSIGNED_BYTE, gl.NEAREST);
    const hdr = this.hdr ? createTarget(gl, w, h, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT, gl.NEAREST) : null;
    const wash = this.hdr ? createTarget(gl, w, h, gl.RGBA16F, gl.RGBA, gl.HALF_FLOAT, gl.NEAREST) : null;
    const layer = this.hdr ? null : createTarget(gl, w, h, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.NEAREST);
    this.targets = { w, h, scene, mask, hdr, wash, layer };
  }

  private disposeTargets(): void {
    if (!this.targets) return;
    const gl = this.gl;
    for (const t of [this.targets.scene, this.targets.mask, this.targets.hdr, this.targets.wash, this.targets.layer])
      deleteTarget(gl, t);
    this.targets = null;
  }

  private bind(t: Target): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, t.fbo);
    gl.viewport(0, 0, t.width, t.height);
  }

  private instanced(program: Program, stride: number, layout: [string, number][]): Instanced {
    const gl = this.gl;
    const vao = gl.createVertexArray()!;
    const buffer = gl.createBuffer()!;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    let offset = 0;
    for (const [name, size] of layout) {
      const loc = program.attribs.get(name);
      if (loc !== undefined && loc >= 0) {
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, size, gl.FLOAT, false, stride * 4, offset * 4);
        gl.vertexAttribDivisor(loc, 1);
      }
      offset += size;
    }
    gl.bindVertexArray(null);
    return { program, vao, buffer };
  }

  private drawInstanced(inst: Instanced, data: Float32Array, stride: number, uniforms: Record<string, Uniform>): void {
    const count = data.length / stride;
    if (count === 0) return;
    const gl = this.gl;
    gl.useProgram(inst.program.program);
    this.setUniforms(inst.program, uniforms);
    gl.bindVertexArray(inst.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, inst.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, count);
    gl.bindVertexArray(null);
  }

  private fullscreen(p: Program, uniforms: Record<string, Uniform>, textures: [string, WebGLTexture][] = []): void {
    const gl = this.gl;
    gl.useProgram(p.program);
    this.bindInputs(p, textures);
    this.setUniforms(p, uniforms);
    gl.bindVertexArray(this.empty);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
  }

  private copy(tex: WebGLTexture): void {
    this.fullscreen(this.p.copy, {}, [['u_tex', tex]]);
  }

  /** Photo on unit 0, depth (or constant white) on unit 1, extra inputs from unit 2. */
  private bindInputs(p: Program, textures: [string, WebGLTexture][]): void {
    const gl = this.gl;
    const photo = p.uniforms.get('u_photo');
    if (photo && this.photo) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.photo.tex);
      gl.uniform1i(photo, 0);
    }
    const depth = p.uniforms.get('u_depth');
    if (depth) {
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, this.depthTex ?? this.white);
      gl.uniform1i(depth, 1);
    }
    textures.forEach(([name, tex], i) => {
      const loc = p.uniforms.get(name);
      if (!loc) return;
      gl.activeTexture(gl.TEXTURE2 + i);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.uniform1i(loc, 2 + i);
    });
    gl.activeTexture(gl.TEXTURE0);
  }

  private setUniforms(p: Program, uniforms: Record<string, Uniform>): void {
    const gl = this.gl;
    for (const [name, value] of Object.entries(uniforms)) {
      const loc = p.uniforms.get(name);
      if (!loc) continue;
      if (typeof value === 'number') gl.uniform1f(loc, value);
      else if (value.length === 2) gl.uniform2f(loc, value[0], value[1]);
      else if (value.length === 3) gl.uniform3f(loc, value[0], value[1], value[2]);
      else if (value.length === 4) gl.uniform4f(loc, value[0], value[1], value[2], value[3]);
      else if (value.length === 9) gl.uniformMatrix3fv(loc, false, Float32Array.from(value));
    }
  }

  private blit(tex: WebGLTexture, rect: number[], clip: number[] | null, solid: number[] | null): void {
    const gl = this.gl;
    const p = this.p.present;
    gl.useProgram(p.program);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.uniform1i(p.uniforms.get('u_tex')!, 0);
    this.setUniforms(p, {
      u_view: [this.canvas.width, this.canvas.height],
      u_rect: rect,
      u_uv: [0, 0, 1, 1],
      u_clip: clip ?? [0, 0, 0],
      u_solid: solid ?? [0, 0, 0, 0],
    });
    gl.bindVertexArray(this.empty);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    gl.bindVertexArray(null);
  }
}

/** Premultiplied 8-bit sRGB color (no linearization): Skia paint color with alpha. */
function premulSrgb(hex: string, a: number): [number, number, number, number] {
  const [r, g, b] = hexToRgb(hex);
  return [(r / 255) * a, (g / 255) * a, (b / 255) * a, a];
}
